/**
 * Inline "Schedules" subform attached to the Service Plan CRUD dialog so
 * the operator can configure schedules (and the checklist items each
 * schedule runs through) at the same time as creating the plan.
 *
 * Create-mode behaviour:
 *   - Stages `_stagedSchedules` onto the parent form values.
 *   - Each staged row carries its schedule metadata + an ordered list of
 *     checklist items.
 *   - `persistStagedServicePlanSchedules()` (registered as the
 *     CrudConfig.afterSave hook) walks the staged list once the parent
 *     Service Plan has been created, POSTing the schedule first, then
 *     the checklist parent row, then each item — mirroring what
 *     `ChecklistEditorDialog` does for existing schedules.
 *
 * Edit-mode behaviour:
 *   - Renders nothing. The Service Plan's relatedLists already show
 *     existing schedules with an "Edit Checklist" link that opens
 *     `ChecklistEditorDialog`, which is the single source of truth for
 *     schedule edits. Surfacing a second editor here would just confuse.
 */
import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { GripVertical, Plus, Trash2 } from 'lucide-react';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from '@/components/ui/toast';
import type { FormExtrasContext } from '@/components/shared/crud-page';

interface StagedItem {
  task: string;
  groupHeader?: string | null;
}
export interface StagedSchedule {
  name: string;
  serviceInterval: string;       // captured as string for the input, parsed at persist time
  unitOfMeasurement: string;
  recurring: boolean;
  // Marks this schedule as the engine-oil row. Server's pre-save hook
  // auto-detects from name when not set, but operators can pin it
  // here at create time so the dashboard's "Engine Oil Due" column
  // doesn't depend on the heuristic catching the right naming.
  isOilChange: boolean;
  items: StagedItem[];
}

// Constrained to the same options the existing checklist-editor dialog
// uses so the downstream Service Report / upcoming-services compute can
// rely on a known set rather than parsing free text.
const UNIT_OPTIONS = ['Distance (km)', 'Hours of Operation', 'Days', 'Months'];

function emptySchedule(): StagedSchedule {
  return { name: '', serviceInterval: '', unitOfMeasurement: '', recurring: false, isOilChange: false, items: [] };
}

export function ServicePlanScheduleSubform({ isEdit, formValues, setFormValues }: FormExtrasContext) {
  // Edit-mode: existing schedules show via relatedLists with "Edit Checklist"
  // links — no need for a second editor here.
  if (isEdit) return null;

  const staged: StagedSchedule[] = formValues._stagedSchedules ?? [];
  const setStaged = (next: StagedSchedule[]) => {
    setFormValues((prev) => ({ ...prev, _stagedSchedules: next }));
  };
  const update = (i: number, patch: Partial<StagedSchedule>) => {
    setStaged(staged.map((s, j) => (j === i ? { ...s, ...patch } : s)));
  };
  const remove = (i: number) => setStaged(staged.filter((_, j) => j !== i));
  const add = () => setStaged([...staged, emptySchedule()]);

  return (
    <div className="border-t pt-4 mt-2 space-y-3">
      <div className="flex items-center justify-between">
        <Label className="text-sm font-semibold">Schedules ({staged.length})</Label>
        <Button type="button" size="sm" variant="outline" onClick={add}>
          <Plus className="h-3 w-3 mr-1" /> Add Schedule
        </Button>
      </div>
      {staged.length === 0 && (
        <p className="text-xs text-muted-foreground">
          No schedules yet. Add one to define the intervals and checklist this plan runs.
        </p>
      )}
      {staged.map((s, i) => (
        <ScheduleCard
          key={i}
          schedule={s}
          index={i}
          onChange={(patch) => update(i, patch)}
          onRemove={() => remove(i)}
        />
      ))}
    </div>
  );
}

function ScheduleCard({
  schedule, index, onChange, onRemove,
}: {
  schedule: StagedSchedule;
  index: number;
  onChange: (patch: Partial<StagedSchedule>) => void;
  onRemove: () => void;
}) {
  function removeItem(i: number) {
    onChange({ items: schedule.items.filter((_, j) => j !== i) });
  }

  // -------- Library-first composer (mirrors checklist-editor-dialog) --------
  // Lets the operator add tasks by picking from the reusable /checklists
  // library OR creating a new one (which both goes into the library and is
  // staged onto this schedule). Without this, the inline subform was the
  // only place in the app where checklist items could only be entered as
  // free text — UX mismatch with the schedule's own edit flow.
  const [composerOpen, setComposerOpen] = React.useState(false);
  const [librarySearchText, setLibrarySearchText] = React.useState('');
  const [debouncedSearch, setDebouncedSearch] = React.useState('');
  React.useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(librarySearchText), 180);
    return () => clearTimeout(t);
  }, [librarySearchText]);

  const [creatingNew, setCreatingNew] = React.useState(false);
  const [newTask, setNewTask] = React.useState('');
  const [newGroup, setNewGroup] = React.useState('');
  const [addingFromLibrary, setAddingFromLibrary] = React.useState(false);

  const librarySearch = useQuery({
    queryKey: ['service-plan-subform', 'library', debouncedSearch],
    queryFn: async () => {
      const qs = new URLSearchParams({ limit: '100', sort: 'listItem', order: 'asc' });
      if (debouncedSearch.trim()) qs.set('search', debouncedSearch.trim());
      const r = await api<Array<{ id: string; listItem: string; checklistGroup?: { id: string; groupName?: string } | null }>>(
        `/checklists?${qs.toString()}`,
      );
      return r.data ?? [];
    },
    enabled: composerOpen,
    staleTime: 30_000,
  });
  const libraryItems = librarySearch.data ?? [];

  // Skip rows already on this staged schedule — same dedupe rule as the
  // editor dialog (case-insensitive listItem text match).
  const alreadyAdded = new Set(schedule.items.map((it) => (it.task ?? '').trim().toLowerCase()));
  const availableLibrary = libraryItems.filter((l) => !alreadyAdded.has((l.listItem ?? '').trim().toLowerCase()));

  function handleAddFromLibrary(lib: { id: string; listItem: string; checklistGroup?: { groupName?: string } | null }) {
    onChange({
      items: [...schedule.items, { task: lib.listItem, groupHeader: lib.checklistGroup?.groupName ?? null }],
    });
  }

  async function handleAddNew() {
    const text = newTask.trim();
    if (!text) return;
    setAddingFromLibrary(true);
    // Push into the library so the task is reusable on future schedules.
    // Swallow duplicate-key collisions — server enforces unique listItem.
    try {
      const r = await api('/checklists', { method: 'POST', body: JSON.stringify({ listItem: text }) });
      if (!r.success && !/duplicate|exists/i.test(r.error?.message ?? '')) {
        toast('Error', r.error?.message ?? 'Failed to add to library', 'error');
      }
    } catch { /* swallow */ }
    setAddingFromLibrary(false);
    onChange({
      items: [...schedule.items, { task: text, groupHeader: newGroup.trim() || null }],
    });
    setNewTask(''); setNewGroup(''); setCreatingNew(false);
  }

  // Drag-and-drop reorder. Mirrors the pattern in checklist-editor-dialog
  // (same native HTML5 drag, no extra dep). Since staged items have no
  // server id yet, we key drag state on array index instead.
  const [dragIdx, setDragIdx] = React.useState<number | null>(null);
  const [overIdx, setOverIdx] = React.useState<number | null>(null);
  function handleDragStart(i: number) { setDragIdx(i); }
  function handleDragOver(e: React.DragEvent, i: number) {
    e.preventDefault();
    if (dragIdx != null && dragIdx !== i) setOverIdx(i);
  }
  function handleDrop(targetIdx: number) {
    if (dragIdx == null || dragIdx === targetIdx) { setDragIdx(null); setOverIdx(null); return; }
    const next = [...schedule.items];
    const [moved] = next.splice(dragIdx, 1);
    next.splice(targetIdx, 0, moved);
    onChange({ items: next });
    setDragIdx(null); setOverIdx(null);
  }
  function endDrag() { setDragIdx(null); setOverIdx(null); }

  return (
    <div className="border rounded-md p-3 space-y-3 bg-muted/20">
      <div className="flex items-start justify-between gap-2">
        <div className="text-xs text-muted-foreground font-semibold">Schedule {index + 1}</div>
        <button
          type="button"
          onClick={onRemove}
          className="text-destructive hover:underline text-xs"
        >Remove</button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-2">
        <div>
          <Label className="text-xs">Name</Label>
          <Input
            value={schedule.name}
            onChange={(e) => onChange({ name: e.target.value })}
            placeholder="e.g. 10,000 km Service"
            className="h-9"
          />
        </div>
        <div>
          <Label className="text-xs">Service Interval</Label>
          <Input
            type="number"
            min={0}
            value={schedule.serviceInterval}
            onChange={(e) => onChange({ serviceInterval: e.target.value })}
            placeholder="e.g. 10000"
            className="h-9"
          />
        </div>
        <div>
          <Label className="text-xs">Unit of Measurement</Label>
          <Select
            value={schedule.unitOfMeasurement || '__none__'}
            onValueChange={(v) => onChange({ unitOfMeasurement: v === '__none__' ? '' : v })}
          >
            <SelectTrigger className="h-9"><SelectValue placeholder="Unit…" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__none__">— None —</SelectItem>
              {UNIT_OPTIONS.map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-end pb-2 gap-4">
          <label className="inline-flex items-center gap-2 text-sm">
            <Checkbox
              checked={schedule.recurring}
              onCheckedChange={(v) => onChange({ recurring: v === true })}
            />
            <span>Recurring</span>
          </label>
          <label className="inline-flex items-center gap-2 text-sm">
            <Checkbox
              checked={schedule.isOilChange}
              onCheckedChange={(v) => onChange({ isOilChange: v === true })}
            />
            <span>Engine Oil Change</span>
          </label>
        </div>
      </div>

      <div className="border-t pt-2">
        <Label className="text-xs">Checklist Items ({schedule.items.length})</Label>
        {schedule.items.length === 0 && (
          <p className="text-[11px] text-muted-foreground mt-1">
            No items yet. Items are tasks a technician ticks through during the service.
          </p>
        )}
        {schedule.items.length > 0 && (
          <ul className="mt-2 space-y-1">
            {schedule.items.map((it, j) => (
              <li
                key={j}
                draggable
                onDragStart={() => handleDragStart(j)}
                onDragOver={(e) => handleDragOver(e, j)}
                onDrop={() => handleDrop(j)}
                onDragEnd={endDrag}
                className={
                  'flex items-center gap-2 rounded-md border bg-card px-2 py-1.5 text-xs cursor-grab active:cursor-grabbing transition-all ' +
                  (dragIdx === j ? 'opacity-50 ' : '') +
                  (overIdx === j && dragIdx !== j ? 'border-primary ring-1 ring-primary/40 ' : '')
                }
                title="Drag to reorder"
              >
                <GripVertical className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0" />
                <span className="w-5 text-muted-foreground text-right">{j + 1}.</span>
                <span className="flex-1 truncate">
                  {it.task}
                  {it.groupHeader && (
                    <span className="ml-2 text-muted-foreground">· {it.groupHeader}</span>
                  )}
                </span>
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); removeItem(j); }}
                  onPointerDown={(e) => e.stopPropagation()}
                  className="text-destructive hover:text-destructive/80 flex-shrink-0"
                  aria-label="Remove item"
                  title="Remove item"
                >
                  <Trash2 className="h-3 w-3" />
                </button>
              </li>
            ))}
          </ul>
        )}
        {/* Library-first composer — matches the ChecklistEditorDialog flow
            so adding items inline while creating a Service Plan offers the
            same picker (library lookup + create-new) as editing an existing
            schedule does. */}
        <div className="mt-2 space-y-2">
          {!composerOpen && (
            <div className="flex justify-end">
              <Button type="button" size="sm" variant="outline" onClick={() => setComposerOpen(true)}>
                <Plus className="h-3 w-3 mr-1" /> Add Item
              </Button>
            </div>
          )}

          {composerOpen && (
            <div className="rounded-md border bg-muted/30 p-3 space-y-3">
              {!creatingNew ? (
                <>
                  <div className="flex gap-2 items-center">
                    <Input
                      value={librarySearchText}
                      onChange={(e) => setLibrarySearchText(e.target.value)}
                      placeholder="Search library…"
                      className="h-8 text-sm flex-1"
                      autoFocus
                    />
                    <Button type="button" size="sm" variant="outline" onClick={() => setCreatingNew(true)}>
                      <Plus className="h-3.5 w-3.5 mr-1" /> Create new
                    </Button>
                  </div>

                  <div className="rounded-md border bg-background max-h-64 overflow-y-auto">
                    {librarySearch.isLoading && (
                      <div className="px-3 py-2 text-xs text-muted-foreground">Loading library…</div>
                    )}
                    {!librarySearch.isLoading && availableLibrary.length === 0 && (
                      <div className="px-3 py-2 text-xs text-muted-foreground">
                        {librarySearchText.trim()
                          ? 'No matching tasks in the library.'
                          : 'Every library task is already on this schedule.'}
                      </div>
                    )}
                    {availableLibrary.map((lib) => (
                      <button
                        key={lib.id}
                        type="button"
                        onClick={() => handleAddFromLibrary(lib)}
                        className="block w-full text-left px-3 py-1.5 text-xs border-b last:border-0 hover:bg-accent hover:text-accent-foreground transition-colors"
                      >
                        <span className="font-medium">{lib.listItem}</span>
                        {lib.checklistGroup?.groupName && (
                          <span className="ml-2 text-muted-foreground">· {lib.checklistGroup.groupName}</span>
                        )}
                      </button>
                    ))}
                  </div>

                  <div className="flex justify-end">
                    <Button type="button" size="sm" variant="ghost" onClick={() => { setComposerOpen(false); setLibrarySearchText(''); }}>
                      Done
                    </Button>
                  </div>
                </>
              ) : (
                <>
                  <div>
                    <Label className="text-xs">Task</Label>
                    <Input
                      value={newTask}
                      onChange={(e) => setNewTask(e.target.value)}
                      placeholder="e.g. Check brake fluid level"
                      className="h-8 text-sm"
                      autoFocus
                      onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAddNew(); } }}
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Group (optional)</Label>
                    <Input
                      value={newGroup}
                      onChange={(e) => setNewGroup(e.target.value)}
                      placeholder="e.g. Engine Bay, Cab Interior, Tyres"
                      className="h-8 text-sm"
                    />
                    <p className="text-[10px] text-muted-foreground mt-1">
                      Section heading shown above this task on the technician's checklist.
                    </p>
                  </div>
                  <div className="flex justify-end gap-2">
                    <Button type="button" size="sm" variant="ghost" onClick={() => { setCreatingNew(false); setNewTask(''); setNewGroup(''); }}>
                      Back
                    </Button>
                    <Button type="button" size="sm" onClick={handleAddNew}
                      disabled={!newTask.trim() || addingFromLibrary}>
                      <Plus className="h-3.5 w-3.5 mr-1" />
                      {addingFromLibrary ? 'Adding…' : 'Add to schedule & library'}
                    </Button>
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * afterSave hook companion — once the new Service Plan exists, walk the
 * staged schedules and persist each one along with its checklist:
 *   1. POST /servicing/schedules     → schedule row (FK to plan)
 *   2. POST /service-plan-schedule-checklists → checklist parent row
 *   3. POST /service-plan-schedule-checklist-items × N → ordered items
 *
 * Failures throw, surfacing in the CrudPage error toast. The plan
 * itself is already saved at this point — partial schedule failures
 * leave behind the rows that did make it; the user can finish the rest
 * via the schedule editor.
 */
export async function persistStagedServicePlanSchedules(
  parentId: string,
  formValues: Record<string, any>,
  isCreate: boolean,
): Promise<void> {
  if (!isCreate) return;
  const staged: StagedSchedule[] = formValues._stagedSchedules ?? [];
  for (const s of staged) {
    if (!s.name?.trim()) continue; // skip rows the user added but never filled in

    // 1. Schedule row. Including `isOilChange` explicitly trips the
    //    model's `isOilChangeAuto` flag to false (per the post-save
    //    hook on ServicePlanSchedule), so the name heuristic won't
    //    overwrite the operator's pick on a future edit.
    const scheduleResp = await api<{ id: string }>('/servicing/schedules', {
      method: 'POST',
      body: JSON.stringify({
        name: s.name.trim(),
        servicePlan: parentId,
        serviceInterval: s.serviceInterval === '' ? null : Number(s.serviceInterval),
        unitOfMeasurement: s.unitOfMeasurement || null,
        recurring: s.recurring,
        isOilChange: s.isOilChange,
      }),
    });
    if (!scheduleResp.success || !scheduleResp.data?.id) {
      throw new Error(scheduleResp.error?.message || `Failed to create schedule "${s.name}"`);
    }
    const scheduleId = scheduleResp.data.id;

    if (s.items.length === 0) continue;

    // 2. Checklist parent row (one per schedule).
    const checklistResp = await api<{ id: string }>('/service-plan-schedule-checklists', {
      method: 'POST',
      body: JSON.stringify({
        servicePlan: parentId,
        servicePlanSchedule: scheduleId,
      }),
    });
    if (!checklistResp.success || !checklistResp.data?.id) {
      throw new Error(checklistResp.error?.message || `Failed to create checklist for "${s.name}"`);
    }
    const checklistId = checklistResp.data.id;

    // 3. Items in order. sortOrder steps by 10 so future inserts have room
    //    without renumbering (matches the existing editor's convention).
    let sortOrder = 10;
    for (const it of s.items) {
      const task = it.task?.trim();
      if (!task) continue;
      const itemResp = await api('/service-plan-schedule-checklist-items', {
        method: 'POST',
        body: JSON.stringify({
          parent: checklistId, task, sortOrder,
          // Carry the group header from library picks (and "Create new"
          // entries that included a group) so the technician sees the same
          // section heading the editor dialog would produce.
          groupHeader: it.groupHeader ?? null,
        }),
      });
      if (!itemResp.success) {
        throw new Error(itemResp.error?.message || `Failed to add item "${task}"`);
      }
      sortOrder += 10;
    }
  }
}
